"use client"
import { Dialog } from "@src/components/Dialog"
import {
  HelpScoutArticleKey,
  helpScoutArticles,
} from "@src/config/helpScoutArticles"
import { usePrivateModeAuth } from "@src/hooks/usePrivateModeAuth"
import { LockIcon } from "@src/icons"
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

type ConfidentialModeContextType = {
  confidentialMode: boolean
  setConfidentialMode: (value: boolean) => void
  toggleConfidentialMode: () => void
  isAuthenticating: boolean
  authError: string | null
  isConnected: boolean
  openModal: () => void
  closeModal: () => void
}

const ConfidentialModeContext = createContext<
  ConfidentialModeContextType | undefined
>(undefined)

export function ConfidentialModeProvider({
  children,
}: { children: ReactNode }) {
  const [modalOpen, setModalOpen] = useState(false)
  const {
    isPrivateModeEnabled,
    isAuthenticating,
    authError,
    isConnected,
    enablePrivateMode,
    disablePrivateMode,
    togglePrivateMode,
  } = usePrivateModeAuth()

  const openModal = useCallback(() => {
    setModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  useEffect(() => {
    document.documentElement.classList.toggle(
      "confidential-mode",
      isPrivateModeEnabled
    )
  }, [isPrivateModeEnabled])

  const setConfidentialMode = useCallback(
    (value: boolean) => {
      if (value) {
        void enablePrivateMode()
        return
      }
      void disablePrivateMode()
    },
    [disablePrivateMode, enablePrivateMode]
  )

  const toggleConfidentialMode = useCallback(() => {
    void togglePrivateMode()
  }, [togglePrivateMode])

  const value = useMemo(
    () => ({
      confidentialMode: isPrivateModeEnabled,
      setConfidentialMode,
      toggleConfidentialMode,
      isAuthenticating,
      authError,
      isConnected,
      openModal,
      closeModal,
    }),
    [
      authError,
      isAuthenticating,
      isConnected,
      isPrivateModeEnabled,
      setConfidentialMode,
      toggleConfidentialMode,
      openModal,
      closeModal,
    ]
  )

  return (
    <ConfidentialModeContext.Provider value={value}>
      {children}

      <Dialog
        open={modalOpen}
        onClose={closeModal}
        header={
          <div className="-mt-9 flex size-12 items-center justify-center self-center rounded-full bg-gray-900 dark:bg-green-500/10">
            <LockIcon className="size-6 text-green-500" />
          </div>
        }
      >
        <h1 className="font-semibold text-base/5 text-gray-900 dark:text-white">
          What is Confidential mode?
        </h1>

        <div className="mt-2 font-medium text-gray-600 text-sm dark:text-gray-400 [&_p]:mt-3 [&_strong]:text-gray-700 dark:[&_strong]:text-gray-200">
          <p>
            Your <strong>Main Account</strong> is a standard blockchain
            account—activity is visible onchain. Your{" "}
            <strong>Confidential Account</strong> keeps balances, activities and
            transfers <strong>private on the blockchain</strong>.
          </p>
          <p>
            You can move funds between accounts at any time. After transferring,
            toggle the app to the destination account to see your funds.
          </p>
          <p>
            Confidential transfers to other accounts may not be permitted in all
            jurisdictions. You are responsible for complying with your local
            laws.
          </p>
          <p>
            <button
              type="button"
              className="font-semibold text-gray-900 underline hover:text-gray-700 dark:text-gray-200 dark:hover:text-white"
              onClick={() => {
                const articleId =
                  helpScoutArticles[HelpScoutArticleKey.CONFIDENTIAL_MODE]
                    .articleId
                if (window.Beacon) {
                  window.Beacon("article", articleId)
                }
              }}
            >
              Click here
            </button>{" "}
            to learn more about Confidential mode.
          </p>
        </div>
      </Dialog>
    </ConfidentialModeContext.Provider>
  )
}

export function useConfidentialMode() {
  const context = useContext(ConfidentialModeContext)

  if (!context) {
    throw new Error(
      "useConfidentialMode must be used within a ConfidentialModeProvider"
    )
  }

  return context
}
